import { observer } from 'mobx-react-lite'
import React, { type KeyboardEvent, useEffect } from 'react'
import classNames from 'classnames'
import { Alert, Box, Button, Error, Icon, InputCheckbox, Tooltip, Typography } from 'shared/ui'
import {
  makeInputPattern,
  makeTextFieldAutoFocus,
  makeTextFieldHighlight,
  TextField,
} from 'shared/ui/TextField'
import { LINK_SUPPORT } from 'shared/constants/links'

import { type TwoFaModalStore } from 'entities/Auth/ui/TwoFaModal'
import { QRCodeContent } from 'entities/Auth/ui/TwoFaModal/ui/Code/QRCodeContent'
import styles from './styles.module.scss'

const InputCode = makeInputPattern({
  format: '######',
  placeholder: '000000',
})

type IProps = {
  store: TwoFaModalStore
}

export const TwoFaCodeModalContent = observer(({ store }: IProps) => {
  const {
    setTwoFaCode,
    twoFaCode,
    loading,
    twoFaMethod,
    twoFaCodeError,
    isValidTwoFaCode,
    onVerifyCode,
    twoFaLabel,
    shouldSendTwoFaCode,
    resendTwoFaCode,
    openMethodModal,
    hasResendTimer,
    twoFaSettings,
    resendText,
    disabledCodeResend,
    twoFaCodeAlertError,
    toggleRemember,
    remember,
    resetTwoCodeModal,
    isChangeSecurityMode,
    isShowVerifyViaOwnerEmail,
    verifyViaOwnerEmail,
  } = store

  useEffect(() => {
    return () => {
      resetTwoCodeModal()
    }
  }, [])

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && isValidTwoFaCode) {
      event.preventDefault()
      onVerifyCode()
    }
  }

  return (
    <>
      <div className={styles.wrap}>
        {isChangeSecurityMode && twoFaMethod?.type === 'app' && <QRCodeContent store={store} />}
        {twoFaCodeAlertError && (
          <Box marginBottom={'24px'}>
            <Alert
              item={{
                type: 'error',
                desc: twoFaCodeAlertError,
              }}
            />
          </Box>
        )}
        <div className={styles.info}>
          <Typography
            color={'var(--content-primary-primary)'}
            variant={'body-md-medium'}
            ariaLabel={'twoFaCodeModalTitle'}
          >
            Enter the 6-digit code
          </Typography>
          <Typography
            color={'var(--content-primary-tertiary)'}
            variant={'body-md-regular'}
            ariaLabel={'twoFaCodeModalLabel'}
          >
            {twoFaLabel}
          </Typography>
        </div>

        <div className={styles.wrapTextField}>
          <TextField
            className={classNames(styles.textField, {
              [styles.error]: twoFaCodeError,
              [styles.disabled]: disabledCodeResend,
            })}
            Input={InputCode}
            InputProps={{ onKeyDown }}
            variant='stroke'
            size='medium'
            value={twoFaCode}
            disabled={loading || disabledCodeResend}
            onChange={setTwoFaCode}
            mainActions={[
              makeTextFieldAutoFocus({ withFocus: true }),
              makeTextFieldHighlight({ mode: 'error', highlight: !!twoFaCodeError }),
            ]}
          />
          <Error type={'text'} error={twoFaCodeError} />
        </div>

        {shouldSendTwoFaCode && !hasResendTimer && (
          <div className={styles.actionItem}>
            <Typography
              color={'var(--content-primary-tertiary)'}
              variant={'body-md-regular'}
              ariaLabel={'resendTwoFaCode'}
            >
              Haven’t received it?
            </Typography>
            <Button
              className={styles.linkBtn}
              typeBtn='link'
              type='button'
              onClick={resendTwoFaCode}
              disabled={loading}
              text={'Resend new code'}
            />
          </div>
        )}
        {!hasResendTimer && twoFaSettings?.length !== 1 && (
          <div className={styles.actionItem}>
            <Typography
              color={'var(--content-primary-tertiary)'}
              variant={'body-md-regular'}
              ariaLabel={'backToMethods'}
            >
              Use a different
            </Typography>
            <Button
              className={styles.linkBtn}
              typeBtn='link'
              type='button'
              onClick={openMethodModal}
              disabled={loading}
              text={'method'}
            />
          </div>
        )}
        {hasResendTimer && (
          <div className={styles.actionItem}>
            <Typography
              color={'var(--content-primary-tertiary)'}
              variant={'body-md-regular'}
              ariaLabel={'resendTimer'}
            >
              {resendText}
            </Typography>
          </div>
        )}
        {isChangeSecurityMode && twoFaMethod?.type === 'app' && (
          <div className={styles.actionItem}>
            <Typography
              color={'var(--content-primary-tertiary)'}
              variant={'body-md-regular'}
              ariaLabel={'resendTimer'}
            >
              Need help?
            </Typography>
            <a href={LINK_SUPPORT} target={'_blank'} rel='noreferrer'>
              <Button
                className={styles.linkBtn}
                typeBtn='link'
                tag={'div'}
                text={'Contact Support'}
              />
            </a>
          </div>
        )}

        {isShowVerifyViaOwnerEmail && (
          <div className={styles.actionItem}>
            <Button
              className={styles.linkBtn}
              typeBtn='link'
              type='button'
              onClick={verifyViaOwnerEmail}
              text={'Verify via organization owner email'}
            />
          </div>
        )}
      </div>
      {!isChangeSecurityMode && (
        <InputCheckbox
          checked={remember}
          onChecked={toggleRemember}
          className={styles.rememberCheckbox}
          customLabel={
            <div className={styles.rememberCheckboxLabel}>
              <Typography
                color={'var(--content-primary-tertiary)'}
                variant={'body-md-regular'}
                ariaLabel={'rememberCheckbox'}
              >
                Don’t ask again for 2 weeks
              </Typography>
              <Tooltip
                placement={'top'}
                label={
                  <span>
                    We won’t ask for a code on
                    <br />
                    this trusted device. Only
                    <br />
                    select this if the device isn’t
                    <br />
                    shared
                  </span>
                }
              >
                <Icon
                  icon={'questionFull'}
                  color={'var(--content-neutral-primary)'}
                  fontSize={12}
                  padding={2}
                />
              </Tooltip>
            </div>
          }
        />
      )}
    </>
  )
})
